import { Component, Inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { LayoutUtilsService, MessageType } from 'src/app/_theme/core/_base/curd/utils/layout-utils.service';
import { getStatusPageErrorMessage } from 'src/app/_theme/core/_base/curd/utils/validators-util';
import { environment } from 'src/environments/environment';
import { ServiceStatusPipe } from '../../pipes/service-status.pipe';

export interface AddServicesToGroupDialogData {
    groupId: string;
    groupName: string;
    existingServiceIds: string[];
    initialSelectedServiceIds?: string[];
}

interface SelectableService {
    id: string;
    name: string;
    description: string;
    status: string;
    selected: boolean;
}

@Component({
    selector: 'app-add-services-to-group-dialog',
    templateUrl: './add-services-to-group-dialog.component.html',
    styleUrls: ['./add-services-to-group-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        MatButtonModule,
        MatCheckboxModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        ServiceStatusPipe
    ]
})
export class AddServicesToGroupDialogComponent implements OnInit {
    services: SelectableService[] = [];
    serviceSearchQuery = '';
    selectedCount = 0;
    isLoading = false;

    constructor(
        public dialogRef: MatDialogRef<AddServicesToGroupDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AddServicesToGroupDialogData,
        private readonly statusPageService: StatusPageService,
        private readonly cdr: ChangeDetectorRef,
        private readonly layoutUtilsService: LayoutUtilsService
    ) { }

    ngOnInit(): void {
        this.loadServices();
    }

    private loadServices(): void {
        this.isLoading = true;

        // Token is resolved internally by StatusPageService â€” no need to read localStorage here
        this.statusPageService.getServices(undefined, 100).subscribe({
            next: (response) => {
                this.services = response.data
                    .filter((service: any) => service?.hasAccess !== false)
                    .filter((service: any) => !this.data.existingServiceIds.includes((service.serviceId || service.id).toString()))
                    .map((service: any) => ({
                        id: (service.serviceId || service.id).toString(),
                        name: service.name,
                        description: service.description,
                        status: service.currentStatusName || 'Operational',
                        selected: (this.data.initialSelectedServiceIds || []).includes((service.serviceId || service.id).toString())
                    }));
                this.updateSelectedCount();
                this.isLoading = false;
                this.cdr.markForCheck();
            },
            error: (error) => {
                const errorMsg = getStatusPageErrorMessage(error, 'Failed to load services');
                this.layoutUtilsService.showActionNotification(errorMsg, MessageType.Error, environment.TOAST_MESSAGE_TIME, true, false);
                this.isLoading = false;
                this.cdr.markForCheck();
            }
        });
    }

    get hasActiveServiceSearch(): boolean {
        return this.serviceSearchQuery.trim().length > 0;
    }

    get filteredServices(): SelectableService[] {
        const query = this.normalizedServiceSearchQuery;
        if (!query) {
            return this.services;
        }

        return this.services.filter(service => this.serviceMatchesSearch(service, query));
    }

    get hasServiceSearchResults(): boolean {
        return this.filteredServices.length > 0;
    }

    onServiceSearchInput(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        this.serviceSearchQuery = input?.value || '';
        this.cdr.markForCheck();
    }

    clearServiceSearch(): void {
        this.serviceSearchQuery = '';
        this.cdr.markForCheck();
    }

    toggleService(service: SelectableService): void {
        service.selected = !service.selected;
        this.updateSelectedCount();
    }

    toggleAll(event: any): void {
        const checked = event.checked;
        this.services.forEach(service => service.selected = checked);
        this.updateSelectedCount();
    }

    onSelectAllEnter(event: KeyboardEvent): void {
        event.preventDefault();
        event.stopPropagation();
        const checked = !this.isAllSelected();
        this.services.forEach(service => service.selected = checked);
        this.updateSelectedCount();
        this.cdr.markForCheck();
    }

    onServiceCheckboxEnter(event: KeyboardEvent, service: SelectableService): void {
        event.preventDefault();
        event.stopPropagation();
        this.toggleService(service);
        this.cdr.markForCheck();
    }

    isAllSelected(): boolean {
        return this.services.length > 0 && this.services.every(s => s.selected);
    }

    isIndeterminate(): boolean {
        const selectedCount = this.services.filter(s => s.selected).length;
        return selectedCount > 0 && selectedCount < this.services.length;
    }

    private updateSelectedCount(): void {
        this.selectedCount = this.services.filter(s => s.selected).length;
    }

    private get normalizedServiceSearchQuery(): string {
        return this.serviceSearchQuery.trim().toLowerCase();
    }

    private serviceMatchesSearch(service: SelectableService, query: string): boolean {
        return [service.name, service.description]
            .some(value => (value || '').toLowerCase().includes(query));
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onAddServices(): void {
        const selectedServiceIds = this.services
            .filter(s => s.selected)
            .map(s => s.id);

        if (selectedServiceIds.length > 0) {
            this.dialogRef.close(selectedServiceIds);
        }
    }
}
